import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Pencil, Plus, RefreshCcw, Trash2 } from "lucide-react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { formatMoney, formatDate } from "@/lib/utils";
import { useLookupValues } from "@/lib/platformConfig";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/calendar";
import { chipParts, monthOwner, pickerOpenMonth, pickerYearRange, toggleOwnMonth } from "@/lib/termCalendar";

// Phase 1 keeps the three term keys used by fee structure lines.
const TERM_KEYS = ["term1", "term2", "term3"];

const monthsBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const months = [];
  let [year, month] = startDate.slice(0, 7).split("-").map(Number);
  const last = endDate.slice(0, 7);
  for (let guard = 0; guard < 36; guard += 1) {
    const value = `${year}-${String(month).padStart(2, "0")}`;
    if (value > last) break;
    months.push(value);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return months;
};

export const monthLabel = (value, language) =>
  new Date(`${value}-01T00:00:00`).toLocaleDateString(language === "ar" ? "ar" : "en", { month: "short" });

// Tenant-entered period names: the Arabic name is used when it was provided.
export const periodLabel = (period, language) => (language === "ar" && period?.label_ar) || period?.label || "";

const toMinor = (text) => Math.round(parseFloat(text) * 100);
const toMajor = (minor) => (minor / 100).toString();

// ``ownerOf(month)`` returns the label of another period holding the month;
// such a chip is disabled (R6.2). ``showYear`` adds the year under the month (R6.3).
function MonthChips({ months, selected, ownerOf, ownerText, showYear = false, onToggle, language, testId }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {months.map((month) => {
        const active = selected.includes(month);
        const owner = !active && ownerOf ? ownerOf(month) : null;
        const elsewhere = owner !== null;
        const parts = showYear ? chipParts(month, language) : null;
        return (
          <button
            key={month}
            type="button"
            onClick={() => onToggle(month)}
            disabled={elsewhere}
            aria-disabled={elsewhere || undefined}
            aria-pressed={active}
            title={elsewhere && ownerText ? ownerText(owner) : undefined}
            data-testid={testId ? `${testId}-${month}` : undefined}
            className={`rounded border px-2 py-1 text-xs ${active ? "border-[var(--accent)] bg-[var(--accent)] text-white" : elsewhere ? "border-dashed border-[var(--subtle)] text-[var(--text-muted)] opacity-50 cursor-not-allowed" : "border-[var(--subtle)]"}`}
          >
            {parts ? (
              <span className="flex flex-col items-center leading-tight">
                <span>{parts.month}</span>
                <span className="text-[10px] opacity-75">{parts.year}</span>
              </span>
            ) : monthLabel(month, language)}
          </button>
        );
      })}
    </div>
  );
}

// ---- Academic Setup: months per term and instalment due dates ----
export function TermCalendarDialog({ year, open, onOpenChange }) {
  const { t, language } = useI18n();
  const qc = useQueryClient();
  const allMonths = useMemo(() => monthsBetween(year?.start_date, year?.end_date), [year]);
  const [terms, setTerms] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [dueDay, setDueDay] = useState("1");

  const termWord = t("feePlan.term");
  React.useEffect(() => {
    if (!open || !year) return;
    const saved = year.periods || [];
    setTerms(TERM_KEYS.map((key, index) => {
      const period = saved.find((p) => p.key === key);
      return {
        key,
        label: period?.label || `${termWord} ${index + 1}`,
        label_ar: period?.label_ar || "",
        months: period?.months || [],
        due: [period?.installment_due_dates?.[0] || "", period?.installment_due_dates?.[1] || ""],
      };
    }));
    setVacations(saved.filter((p) => p.type === "vacation").map((p) => ({ key: p.key, label: p.label, label_ar: p.label_ar || "", months: p.months || [] })));
    setDueDay(String(year.monthly_due_day || 1));
  }, [open, year, termWord]);

  // A month belongs to one period; a month held elsewhere must be unticked
  // there first (R6.2).
  const toggle = (kind, index, month) => {
    const next = toggleOwnMonth(terms, vacations, kind, index, month);
    setTerms(next.terms);
    setVacations(next.vacations);
  };
  const ownerOf = (kind, index) => (month) => monthOwner(month, terms, vacations, { kind, index }, language);
  const ownerText = (owner) => t("feePlan.monthTakenBy", { period: owner });
  const yearRange = pickerYearRange(year);

  const invalid = terms.some((row) => row.months.length > 0 && !row.label.trim())
    || terms.some((row) => row.due[1] && !row.due[0])
    || terms.some((row) => row.due[0] && row.due[1] && row.due[1] < row.due[0])
    || vacations.some((row) => !row.label.trim())
    || !(Number(dueDay) >= 1 && Number(dueDay) <= 28 && Number.isInteger(Number(dueDay)));

  const save = useMutation({
    mutationFn: async () => unwrap(await api.put(`/academic-years/${year.id}/periods`, {
      periods: [
        ...terms.filter((row) => row.months.length > 0).map((row) => ({
          key: row.key, label: row.label.trim(), label_ar: row.label_ar.trim(), type: "term", months: row.months,
          installment_due_dates: row.due.filter(Boolean),
        })),
        ...vacations.map((row, index) => ({
          key: row.key || `vacation${index + 1}`, label: row.label.trim(), label_ar: row.label_ar.trim(), type: "vacation", months: row.months,
        })),
      ],
      monthly_due_day: Number(dueDay),
    })),
    onSuccess: () => {
      toast.success(t("feePlan.calendarSaved"));
      qc.invalidateQueries({ queryKey: ["ay"] });
      qc.invalidateQueries({ queryKey: ["fee-summary"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="term-calendar-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("feePlan.termCalendar")} · {year?.name}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[var(--text-muted)]">{t("feePlan.termCalendarHint")}</p>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pe-1">
          {terms.map((row, index) => (
            <div key={row.key} className="rounded border border-[var(--subtle)] p-3 space-y-2" data-testid={`calendar-${row.key}`}>
              <div className="flex flex-wrap gap-2">
                <Input
                  value={row.label}
                  onChange={(e) => setTerms((rows) => rows.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)))}
                  className="h-8 max-w-[14rem]"
                  aria-label={t("feePlan.term")}
                />
                <Input
                  value={row.label_ar}
                  dir="rtl"
                  placeholder={t("feePlan.arabicName")}
                  onChange={(e) => setTerms((rows) => rows.map((r, i) => (i === index ? { ...r, label_ar: e.target.value } : r)))}
                  className="h-8 max-w-[14rem]"
                  aria-label={t("feePlan.arabicName")}
                  data-testid={`calendar-${row.key}-label-ar`}
                />
              </div>
              <MonthChips months={allMonths} selected={row.months} ownerOf={ownerOf("term", index)} ownerText={ownerText} showYear onToggle={(m) => toggle("term", index, m)} language={language} testId={`calendar-${row.key}-months`} />
              <div>
                <div className="text-xs font-medium">{t("feePlan.termFeesDueTitle")}</div>
                <div className="text-xs text-[var(--text-muted)]">{t("feePlan.termFeesDueHint")}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[0, 1].map((slot) => (
                  <div key={slot}>
                    <Label className="text-xs uppercase">{t("feePlan.installmentDue", { n: slot + 1 })}</Label>
                    <DatePicker
                      value={row.due[slot]}
                      onChange={(value) => setTerms((rows) => rows.map((r, i) => (i === index ? { ...r, due: r.due.map((d, s) => (s === slot ? value : d)) } : r)))}
                      defaultMonth={pickerOpenMonth(row, slot)}
                      min={slot > 0 ? row.due[0] || undefined : undefined}
                      fromYear={yearRange.fromYear}
                      toYear={yearRange.toYear}
                      placeholder={t("feePlan.pickDate")}
                      ariaLabel={t("feePlan.installmentDue", { n: slot + 1 })}
                      clearable
                      clearLabel={t("feePlan.clearDate")}
                      testId={`calendar-${row.key}-due-${slot + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {vacations.map((row, index) => (
            <div key={`vacation-${index}`} className="rounded border border-dashed border-[var(--subtle)] p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={row.label}
                  placeholder={t("feePlan.vacationLabel")}
                  onChange={(e) => setVacations((rows) => rows.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)))}
                  className="h-8 max-w-[11rem]"
                />
                <Input
                  value={row.label_ar}
                  dir="rtl"
                  placeholder={t("feePlan.arabicName")}
                  onChange={(e) => setVacations((rows) => rows.map((r, i) => (i === index ? { ...r, label_ar: e.target.value } : r)))}
                  className="h-8 max-w-[11rem]"
                  aria-label={t("feePlan.arabicName")}
                />
                <Badge variant="outline">{t("feePlan.notBilled")}</Badge>
                <Button type="button" size="sm" variant="ghost" className="ms-auto" onClick={() => setVacations((rows) => rows.filter((_, i) => i !== index))} aria-label={t("feePlan.remove")}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <MonthChips months={allMonths} selected={row.months} ownerOf={ownerOf("vacation", index)} ownerText={ownerText} showYear onToggle={(m) => toggle("vacation", index, m)} language={language} testId={`calendar-vacation${index + 1}-months`} />
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={() => setVacations((rows) => [...rows, { key: "", label: "", label_ar: "", months: [] }])} data-testid="calendar-add-vacation">
            <Plus className="h-3.5 w-3.5 me-1" /> {t("feePlan.addVacation")}
          </Button>
          <div className="rounded border border-[var(--subtle)] p-3">
            <div className="text-xs font-medium mb-1">{t("feePlan.monthlyFeesDueTitle")}</div>
            <Label className="text-xs uppercase" htmlFor="calendar-monthly-due-day">{t("feePlan.monthlyDueDay")}</Label>
            <Input id="calendar-monthly-due-day" type="number" min="1" max="28" step="1" value={dueDay} onChange={(e) => setDueDay(e.target.value)} className="mt-1 h-8 w-24" data-testid="calendar-monthly-due-day" />
            <div className="text-xs text-[var(--text-muted)] mt-1">{t("feePlan.monthlyDueDayHint")}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || invalid} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="calendar-save">
            {save.isPending ? t("common.saving") : t("feePlan.saveCalendar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Term balance ----
const positionText = (row, t) => {
  if (row.due_now_paise > 0) return row.overdue_since ? t("feePlan.overdueSince", { date: formatDate(row.overdue_since) }) : t("feePlan.dueNow");
  if (row.upcoming_paise > 0) return t("feePlan.dueOn", { date: formatDate(row.next_due_date) });
  if (row.balance_paise === 0 && row.paid_paise > 0) return t("feePlan.settled");
  return "—";
};

export function BalanceTotals({ totals, compact = false }) {
  const { t } = useI18n();
  const cells = [
    ["previous", t("feePlan.previousBalance"), totals?.previous_balance_paise || 0, "text-[var(--error)]"],
    ["due-now", t("feePlan.dueNow"), totals?.current_due_paise || 0, "text-[var(--accent)]"],
    ["total-due", t("feePlan.totalDue"), totals?.total_due_paise || 0, "font-bold"],
    ["upcoming", t("feePlan.upcoming"), totals?.upcoming_paise || 0, "text-[var(--text-muted)]"],
  ];
  return (
    <div className={`grid grid-cols-2 ${compact ? "" : "sm:grid-cols-4"} gap-3`}>
      {cells.map(([key, label, amount, tone]) => (
        <div key={key} data-testid={`balance-${key}`}>
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
          <div className={`font-mono-num font-semibold ${tone}`}>{formatMoney(amount)}</div>
        </div>
      ))}
    </div>
  );
}

export function TermBalanceCard({ studentId, academicYearId, title, staff = true }) {
  const { t, language } = useI18n();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["fee-summary", studentId, academicYearId],
    queryFn: async () => unwrap(await api.get(`/students/${studentId}/fee-summary`, { params: { academic_year_id: academicYearId } })),
    enabled: Boolean(studentId && academicYearId),
  });
  if (!studentId || !academicYearId || isError) return null;
  const rows = data?.rows || [];
  // Parents only see the card once the school tracks dues for the year.
  if (!staff && !isLoading && (rows.length === 0 || !data?.calendar_configured)) return null;
  return (
    <Card className="border-[var(--subtle)]" data-testid="term-balance-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-display">{title || t("feePlan.termBalance")}</CardTitle>
        {data?.as_of && <div className="text-xs text-[var(--text-muted)]">{t("feePlan.asOf", { date: formatDate(data.as_of) })}</div>}
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? <Skeleton className="h-24" /> : (
          <>
            <BalanceTotals totals={data?.totals} />
            {staff && data && !data.calendar_configured && (
              <div className="text-sm text-[var(--text-muted)]" data-testid="term-balance-no-calendar">{t("feePlan.calendarMissing")}</div>
            )}
            {rows.length === 0 ? (
              <div className="text-sm text-[var(--text-muted)]">{t("feePlan.noDues")}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm grid-table min-w-[36rem]">
                  <thead>
                    <tr>
                      <th className="text-start px-3 py-2">{t("feePlan.term")}</th>
                      <th className="text-start px-3 py-2">{t("feePlan.feeHead")}</th>
                      <th className="text-end px-3 py-2">{t("feePlan.expected")}</th>
                      <th className="text-end px-3 py-2">{t("feePlan.discount")}</th>
                      <th className="text-end px-3 py-2">{t("feePlan.paid")}</th>
                      <th className="text-end px-3 py-2">{t("feePlan.balance")}</th>
                      <th className="text-start px-3 py-2">{t("feePlan.position")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.term_key}-${row.fee_head_id}`} data-testid={`balance-row-${row.term_key || "one-time"}-${row.fee_head_code}`}>
                        <td className="px-3 py-2">{(language === "ar" && row.term_label_ar) || row.term_label || t(row.kind === "other" ? "feePlan.otherCharges" : "feePlan.oneTime")}</td>
                        <td className="px-3 py-2">
                          {row.fee_head_name}
                          {row.source === "custom" && <Badge variant="outline" className="ms-2">{t("feePlan.custom")}</Badge>}
                        </td>
                        <td className="px-3 py-2 text-end font-mono-num">{formatMoney(row.expected_paise)}</td>
                        <td className="px-3 py-2 text-end font-mono-num">{row.discount_paise ? formatMoney(row.discount_paise) : "—"}</td>
                        <td className="px-3 py-2 text-end font-mono-num text-[var(--success)]">{formatMoney(row.paid_paise)}</td>
                        <td className="px-3 py-2 text-end font-mono-num font-semibold">{formatMoney(row.balance_paise)}</td>
                        <td className={`px-3 py-2 text-xs ${row.due_now_paise > 0 ? "text-[var(--error)]" : "text-[var(--text-muted)]"}`}>{positionText(row, t)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Student fee plan ----
function CustomiseDialog({ line, periods, studentId, academicYearId, open, onOpenChange }) {
  const { t, language } = useI18n();
  const qc = useQueryClient();
  const reasons = useLookupValues("fee_custom_reasons", { enabled: open && Boolean(line?.allow_custom) });
  const isTerm = line?.frequency === "term";
  const termPeriods = useMemo(() => Object.fromEntries((periods || []).filter((p) => p.type === "term").map((p) => [p.key, p])), [periods]);
  const termKeys = TERM_KEYS.filter((key) => line?.standard_termAmounts?.[key] != null);
  const [included, setIncluded] = useState(true);
  const [startTerm, setStartTerm] = useState("term1");
  const [amounts, setAmounts] = useState({});
  const [months, setMonths] = useState({});
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  React.useEffect(() => {
    if (!open || !line) return;
    setIncluded(Boolean(line.included));
    setStartTerm(line.effective_from_term || "term1");
    setAmounts(Object.fromEntries((line.custom_terms || []).map((key) => [key, toMajor(line.termAmounts[key])])));
    setMonths(line.covered_months || {});
    setAmount(!isTerm && line.is_custom ? toMajor(line.amount_paise) : "");
    setReason(line.reason_code || "");
    setNote(line.note || "");
  }, [open, line, isTerm]);

  const range = (standard) => {
    const min = line?.custom_min_bps == null ? null : Math.floor((standard * line.custom_min_bps) / 10000);
    const max = line?.custom_max_bps == null ? null : Math.floor((standard * line.custom_max_bps) / 10000);
    return { min, max };
  };
  const outOfRange = (text, standard) => {
    if (text === "" || text == null) return false;
    const value = toMinor(text);
    if (!Number.isFinite(value) || value < 0) return true;
    const { min, max } = range(standard);
    return (min != null && value < min) || (max != null && value > max);
  };
  const pickMonth = (termKey, month) => {
    const termMonths = termPeriods[termKey]?.months || [];
    const current = months[termKey] || [];
    const next = current.includes(month) ? current.filter((m) => m !== month) : [...current, month].sort();
    setMonths({ ...months, [termKey]: next });
    // Pro-rata suggestion; staff may overwrite it.
    if (next.length > 0 && termMonths.length > 0) {
      const suggested = Math.round((line.standard_termAmounts[termKey] * next.length) / termMonths.length);
      setAmounts({ ...amounts, [termKey]: toMajor(suggested) });
    }
  };

  const customTerms = termKeys.filter((key) => amounts[key] !== undefined && amounts[key] !== "");
  const hasCustom = line?.allow_custom && included && (isTerm ? customTerms.length > 0 : amount !== "");
  const invalid = hasCustom && (!reason
    || (isTerm ? customTerms.some((key) => outOfRange(amounts[key], line.standard_termAmounts[key])) : outOfRange(amount, line.standard_amount_paise)));

  const save = useMutation({
    mutationFn: async () => {
      const entry = { fee_head_id: line.fee_head_id };
      if (line.is_optional) {
        entry.included = included;
        if (included && isTerm && startTerm !== "term1") entry.effective_from_term = startTerm;
      }
      if (hasCustom) {
        entry.reason_code = reason;
        if (note.trim()) entry.note = note.trim();
        if (isTerm) {
          entry.term_amounts = Object.fromEntries(customTerms.map((key) => [key, toMinor(amounts[key])]));
          const covered = Object.fromEntries(customTerms.filter((key) => (months[key] || []).length > 0).map((key) => [key, months[key]]));
          if (Object.keys(covered).length) entry.covered_months = covered;
        } else {
          entry.amount_paise = toMinor(amount);
        }
      }
      return unwrap(await api.put(`/students/${studentId}/fee-plan`, { academic_year_id: academicYearId, entries: [entry] }));
    },
    onSuccess: () => {
      toast.success(t("feePlan.planSaved"));
      ["fee-plan", "fee-summary", "student-fee-selection"].forEach((key) => qc.invalidateQueries({ queryKey: [key] }));
      onOpenChange(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!line) return null;
  const reasonLabel = (row) => (language === "ar" && row.metadata?.label_ar) || row.label;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="fee-plan-customise-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("feePlan.customiseTitle", { name: line.fee_head_name })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pe-1">
          {line.is_optional && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={included} onCheckedChange={setIncluded} data-testid="fee-plan-included" />
                {t("feePlan.appliesToStudent")}
              </label>
              {included && isTerm && (
                <div>
                  <Label className="text-xs uppercase">{t("feePlan.effectiveFrom")}</Label>
                  <Select value={startTerm} onValueChange={setStartTerm}>
                    <SelectTrigger className="mt-1" data-testid="fee-plan-start-term"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {termKeys.map((key) => <SelectItem key={key} value={key}>{periodLabel(termPeriods[key], language) || key}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          {line.allow_custom && included && isTerm && termKeys.map((key) => {
            const standard = line.standard_termAmounts[key];
            const { min, max } = range(standard);
            // A custom amount applies only to a term not yet charged (R5.3).
            const charged = (line.charged_periods || []).includes(key);
            return (
              <div key={key} className="rounded border border-[var(--subtle)] p-3 space-y-2" data-testid={`fee-plan-${key}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-sm font-medium">
                    {periodLabel(termPeriods[key], language) || key}
                    {charged && <Badge variant="outline" className="ms-2">{t("feePlan.alreadyCharged")}</Badge>}
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {t("feePlan.standardAmount", { amount: formatMoney(standard) })}
                    {(min != null || max != null) && ` · ${t("feePlan.allowedRange", { min: formatMoney(min ?? 0), max: max == null ? "∞" : formatMoney(max) })}`}
                  </div>
                </div>
                {(termPeriods[key]?.months || []).length > 0 && (
                  <div>
                    <div className="text-xs text-[var(--text-muted)] mb-1">{t("feePlan.coveredMonths")}</div>
                    <MonthChips months={termPeriods[key].months} selected={months[key] || []} onToggle={(m) => !charged && pickMonth(key, m)} language={language} testId={`fee-plan-${key}-months`} />
                  </div>
                )}
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amounts[key] ?? ""}
                  placeholder={toMajor(standard)}
                  disabled={charged}
                  onChange={(e) => setAmounts({ ...amounts, [key]: e.target.value })}
                  aria-invalid={outOfRange(amounts[key], standard)}
                  className={outOfRange(amounts[key], standard) ? "border-[var(--error)]" : ""}
                  data-testid={`fee-plan-${key}-amount`}
                />
                {charged && <div className="text-xs text-[var(--text-muted)]" data-testid={`fee-plan-${key}-charged`}>{t("feePlan.chargedLocked")}</div>}
              </div>
            );
          })}
          {line.allow_custom && included && !isTerm && (
            <div>
              <Label className="text-xs uppercase">{t("feePlan.amount")}</Label>
              <Input type="number" min="0" step="0.01" value={amount} placeholder={toMajor(line.standard_amount_paise)} disabled={(line.charged_periods || []).includes("one_time")} onChange={(e) => setAmount(e.target.value)} className={`mt-1 ${outOfRange(amount, line.standard_amount_paise) ? "border-[var(--error)]" : ""}`} data-testid="fee-plan-amount" />
              {(line.charged_periods || []).includes("one_time") && <div className="text-xs text-[var(--text-muted)] mt-1">{t("feePlan.chargedLocked")}</div>}
              <div className="text-xs text-[var(--text-muted)] mt-1">{t("feePlan.standardAmount", { amount: formatMoney(line.standard_amount_paise) })}</div>
            </div>
          )}
          {hasCustom && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">{t("feePlan.reason")}<span className="ms-1 text-[var(--error)]" aria-hidden="true">*</span></Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="mt-1" data-testid="fee-plan-reason"><SelectValue placeholder={t("feePlan.selectReason")} /></SelectTrigger>
                  <SelectContent>
                    {(reasons.data?.values || []).map((row) => <SelectItem key={row.value} value={row.value}>{reasonLabel(row)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase">{t("feePlan.note")}</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={240} className="mt-1" data-testid="fee-plan-note" />
              </div>
            </div>
          )}
          {line.allow_custom && included && <div className="text-xs text-[var(--text-muted)]">{t("feePlan.blankKeepsStandard")}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || invalid} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="fee-plan-save">
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FeePlanCard({ studentId, academicYearId, canEdit = false }) {
  const { t, language } = useI18n();
  const [editing, setEditing] = useState(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["fee-plan", studentId, academicYearId],
    queryFn: async () => unwrap(await api.get(`/students/${studentId}/fee-plan`, { params: { academic_year_id: academicYearId } })),
    enabled: Boolean(studentId && academicYearId),
    retry: false,
  });
  if (!studentId || !academicYearId || isError) return null;
  const termLabel = (key) => periodLabel((data?.periods || []).find((p) => p.key === key), language) || key;
  const typeText = (line) => {
    if (line.is_optional) return line.included ? t("feePlan.optionalEnabled") : t("feePlan.optionalOff");
    return line.is_custom ? t("feePlan.custom") : t("feePlan.standard");
  };
  return (
    <Card className="border-[var(--subtle)]" data-testid="fee-plan-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-display">{t("feePlan.feePlan")}</CardTitle>
        {data?.structure && <div className="text-xs text-[var(--text-muted)]">{data.structure.name}</div>}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? <Skeleton className="h-24 m-4" /> : !data?.structure ? (
          <div className="p-4 text-sm text-[var(--text-muted)]">{t("feePlan.noStructure")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm grid-table min-w-[34rem]">
              <thead>
                <tr>
                  <th className="text-start px-3 py-2">{t("feePlan.feeHead")}</th>
                  <th className="text-start px-3 py-2">{t("feePlan.amount")}</th>
                  <th className="text-start px-3 py-2">{t("feePlan.type")}</th>
                  <th className="px-3 py-2 w-12" />
                </tr>
              </thead>
              <tbody>
                {data.lines.map((line) => (
                  <tr key={line.fee_head_id} className={line.included ? "" : "opacity-60"} data-testid={`fee-plan-line-${line.fee_head_code}`}>
                    <td className="px-3 py-2">{line.fee_head_name}</td>
                    <td className="px-3 py-2 font-mono-num text-xs">
                      {line.frequency === "term"
                        ? TERM_KEYS.filter((key) => line.termAmounts?.[key] != null).map((key) => (
                          <div key={key} className={line.custom_terms.includes(key) ? "text-[var(--accent)] font-semibold" : ""}>
                            {termLabel(key)}: {formatMoney(line.termAmounts[key])}
                          </div>
                        ))
                        : <span className={line.is_custom ? "text-[var(--accent)] font-semibold" : ""}>{formatMoney(line.amount_paise)}{line.frequency === "monthly" && ` ${t("feePlan.perMonth")}`}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline">{typeText(line)}</Badge>
                      {line.charge_when_bought && <div className="text-xs text-[var(--text-muted)] mt-1">{t("feePlan.chargeWhenBought")}</div>}
                      {line.is_optional && line.included && line.effective_from_term && <div className="text-xs text-[var(--text-muted)] mt-1">{t("feePlan.effectiveFrom")}: {termLabel(line.effective_from_term)}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {canEdit && (line.is_optional || line.allow_custom) && (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(line)} aria-label={t("feePlan.customise")} data-testid={`fee-plan-edit-${line.fee_head_code}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <CustomiseDialog line={editing} periods={data?.periods} studentId={studentId} academicYearId={academicYearId} open={Boolean(editing)} onOpenChange={(value) => !value && setEditing(null)} />
    </Card>
  );
}

// ---- Fees > Reports: balance by term for every student ----
export function TermBalanceReport({ canWrite = false }) {
  const { t, language } = useI18n();
  const qc = useQueryClient();
  const [yearId, setYearId] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [preview, setPreview] = useState(null);
  const { data: years } = useQuery({ queryKey: ["ay"], queryFn: async () => unwrap(await api.get("/academic-years")) });
  React.useEffect(() => {
    if (!yearId && years?.length) setYearId((years.find((y) => y.is_current) || years[0]).id);
  }, [years, yearId]);
  const { data, isLoading } = useQuery({
    queryKey: ["report-term-balance", yearId, overdueOnly],
    queryFn: async () => unwrap(await api.get("/reports/term-balance", { params: { academic_year_id: yearId, overdue_only: overdueOnly } })),
    enabled: Boolean(yearId),
  });
  const sync = useMutation({
    mutationFn: async (dryRun) => unwrap(await api.post("/fees/dues/sync", { academic_year_id: yearId, dry_run: dryRun })),
    onSuccess: (report) => {
      if (report.dry_run) { setPreview(report); return; }
      setPreview(null);
      toast.success(t("feePlan.duesApplied"));
      ["report-term-balance", "fee-summary", "fee-plan"].forEach((key) => qc.invalidateQueries({ queryKey: [key] }));
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const download = async () => {
    try {
      const resp = await api.get("/reports/term-balance.xlsx", { params: { academic_year_id: yearId, lang: language, overdue_only: overdueOnly }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url; link.download = `term-balance-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("finance.downloadFailed")); }
  };
  const terms = data?.terms || [];
  return (
    <Card className="border-[var(--subtle)]" data-testid="term-balance-report">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="text-sm font-display">{t("feePlan.termBalance")}</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={yearId} onValueChange={(value) => { setYearId(value); setPreview(null); }}>
            <SelectTrigger className="h-8 w-40" data-testid="term-balance-year"><SelectValue /></SelectTrigger>
            <SelectContent>{(years || []).map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} className="accent-[var(--accent)]" data-testid="term-balance-overdue-only" />
            {t("feePlan.overdueOnly")}
          </label>
          <Button size="sm" variant="outline" onClick={download} disabled={!yearId} data-testid="term-balance-export"><Download className="h-3.5 w-3.5 me-1" />{t("feePlan.exportXlsx")}</Button>
          {canWrite && (
            <Button size="sm" variant="outline" onClick={() => sync.mutate(true)} disabled={!yearId || sync.isPending} data-testid="dues-preview">
              <RefreshCcw className="h-3.5 w-3.5 me-1" />{t("feePlan.buildDues")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {preview && (
          <div className="rounded border border-[var(--subtle)] p-3 text-sm space-y-2" data-testid="dues-preview-panel">
            {!preview.calendar_configured ? <div>{t("feePlan.calendarMissing")}</div> : (
              <>
                <div>{t("feePlan.duesPreview", { students: preview.students, plans: preview.plans_created, created: preview.dues_created, updated: preview.dues_updated, voided: preview.dues_voided })}</div>
                {preview.unmatched_count > 0 && (
                  <div className="text-[var(--error)]">
                    {t("feePlan.duesUnmatched", { count: preview.unmatched_count })}
                    <ul className="mt-1 text-xs font-mono-num text-[var(--text-primary)]">
                      {preview.unmatched.slice(0, 10).map((item, index) => <li key={index}>{item.admission_no} · {item.fee_head_code} · {item.invoice_no || item.period_key} · {formatMoney(item.balance_paise)}</li>)}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => sync.mutate(false)} disabled={sync.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="dues-apply">{t("feePlan.applyDues")}</Button>
                  <Button size="sm" variant="outline" onClick={() => setPreview(null)}>{t("common.cancel")}</Button>
                </div>
              </>
            )}
          </div>
        )}
        {isLoading ? <Skeleton className="h-24" /> : data && !data.calendar_configured ? (
          <div className="text-sm text-[var(--text-muted)]">{t("feePlan.calendarMissing")}</div>
        ) : (
          <>
            <BalanceTotals totals={data?.totals} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm grid-table min-w-[44rem]">
                <thead>
                  <tr>
                    <th className="text-start px-3 py-2">{t("feePlan.student")}</th>
                    {terms.map((term) => <th key={term.key} className="text-end px-3 py-2">{periodLabel(term, language)}</th>)}
                    <th className="text-end px-3 py-2">{t("feePlan.oneTime")}</th>
                    <th className="text-end px-3 py-2">{t("feePlan.previousBalance")}</th>
                    <th className="text-end px-3 py-2">{t("feePlan.dueNow")}</th>
                    <th className="text-end px-3 py-2">{t("feePlan.totalDue")}</th>
                    <th className="text-end px-3 py-2">{t("feePlan.upcoming")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.rows || []).map((row) => (
                    <tr key={row.student_id} data-testid={`term-balance-row-${row.admission_no}`}>
                      <td className="px-3 py-2">
                        <div>{row.student_name}</div>
                        <div className="text-xs text-[var(--text-muted)] font-mono-num">{row.admission_no}</div>
                      </td>
                      {terms.map((term) => <td key={term.key} className="px-3 py-2 text-end font-mono-num">{formatMoney(row.terms[term.key]?.balance_paise || 0)}</td>)}
                      <td className="px-3 py-2 text-end font-mono-num">{formatMoney(row.other.balance_paise)}</td>
                      <td className="px-3 py-2 text-end font-mono-num text-[var(--error)]">{formatMoney(row.previous_balance_paise)}</td>
                      <td className="px-3 py-2 text-end font-mono-num">{formatMoney(row.current_due_paise)}</td>
                      <td className="px-3 py-2 text-end font-mono-num font-semibold">{formatMoney(row.total_due_paise)}</td>
                      <td className="px-3 py-2 text-end font-mono-num text-[var(--text-muted)]">{formatMoney(row.upcoming_paise)}</td>
                    </tr>
                  ))}
                  {(data?.rows || []).length === 0 && (
                    <tr><td colSpan={terms.length + 6} className="px-3 py-6 text-center text-[var(--text-muted)]">{t("feePlan.noDues")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Custom fees report ----
export function CustomFeesReport() {
  const { t, language } = useI18n();
  const [yearId, setYearId] = useState("");
  const reasons = useLookupValues("fee_custom_reasons");
  const { data: years } = useQuery({ queryKey: ["ay"], queryFn: async () => unwrap(await api.get("/academic-years")) });
  React.useEffect(() => {
    if (!yearId && years?.length) setYearId((years.find((y) => y.is_current) || years[0]).id);
  }, [years, yearId]);
  const { data, isLoading } = useQuery({
    queryKey: ["report-custom-fees", yearId],
    queryFn: async () => unwrap(await api.get("/reports/custom-fees", { params: { academic_year_id: yearId } })),
    enabled: Boolean(yearId),
  });
  const reasonLabel = (code) => {
    const row = (reasons.data?.values || []).find((value) => value.value === code);
    return row ? ((language === "ar" && row.metadata?.label_ar) || row.label) : code || "—";
  };
  const covered = (row) => {
    if (row.covered_months?.length) return row.covered_months.map((m) => monthLabel(m, language)).join(" ");
    return row.term_key ? t("feePlan.term") + " " + row.term_key.replace("term", "") : t("feePlan.oneTime");
  };
  const rows = data?.rows || [];
  return (
    <Card className="border-[var(--subtle)]" data-testid="custom-fees-report">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="text-sm font-display">{t("customFee.report")}</CardTitle>
        <Select value={yearId} onValueChange={setYearId}>
          <SelectTrigger className="h-8 w-40" data-testid="custom-fees-year"><SelectValue /></SelectTrigger>
          <SelectContent>{(years || []).map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-24" /> : rows.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)]" data-testid="custom-fees-empty">{t("customFee.noCustomFees")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-[var(--text-muted)] text-start border-b border-[var(--subtle)]">
                  <th className="text-start py-2 pe-3">{t("feePlan.student")}</th>
                  <th className="text-start py-2 pe-3">{t("feePlan.feeHead")}</th>
                  <th className="text-start py-2 pe-3">{t("customFee.covers")}</th>
                  <th className="text-end py-2 pe-3">{t("feePlan.standard")}</th>
                  <th className="text-end py-2 pe-3">{t("customFee.charged")}</th>
                  <th className="text-end py-2 pe-3">{t("customFee.difference")}</th>
                  <th className="text-start py-2 pe-3">{t("feePlan.reason")}</th>
                  <th className="text-start py-2 pe-3">{t("customFee.enteredBy")}</th>
                  <th className="text-start py-2">{t("customFee.invoice")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.invoice_no}-${row.fee_head_id}-${index}`} className="border-b border-[var(--subtle)] align-top" data-testid="custom-fees-row">
                    <td className="py-2 pe-3">{row.student_name}<div className="text-xs text-[var(--text-muted)] font-mono-num">{row.admission_no}</div></td>
                    <td className="py-2 pe-3">{row.fee_head_name}</td>
                    <td className="py-2 pe-3">{covered(row)}</td>
                    <td className="py-2 pe-3 text-end font-mono-num">{formatMoney(row.standard_paise || 0)}</td>
                    <td className="py-2 pe-3 text-end font-mono-num">{formatMoney(row.charged_paise)}</td>
                    <td className={`py-2 pe-3 text-end font-mono-num ${row.difference_paise < 0 ? "text-[var(--error)]" : ""}`}>{formatMoney(row.difference_paise)}</td>
                    <td className="py-2 pe-3">{reasonLabel(row.reason_code)}{row.note && <div className="text-xs text-[var(--text-muted)]">{row.note}</div>}</td>
                    <td className="py-2 pe-3">{row.entered_by}<div className="text-xs text-[var(--text-muted)]">{row.date ? formatDate(row.date) : ""}</div></td>
                    <td className="py-2 font-mono-num">{row.invoice_no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
